import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { Box, Button } from "@mui/material";
import { useDeletePartyAddressMutation } from "Api/Mutations/Party/useDeletePartyAddressMutation";
import { ConfirmDialog } from "Components/Shared/Dialogs/ConfirmDialog";
import { FormSection } from "Components/Shared/FormSection";
import { AddressInput } from "Components/Shared/Inputs/Form/AddressInput";
import { Dropdown } from "Components/Shared/Inputs/Form/Dropdown";
import { FormInput } from "Components/Shared/Inputs/Form/FormInput";
import type {
  AddressFormData,
  PartyFormData,
  PartyFormDataInput,
} from "Components/Users/schemas";
import { useAddressTypeList } from "Hooks/Options/useAddressTypeList";
import { useConfirmDialog } from "Hooks/useConfirmDialog";
import { useCheckPermission } from "Hooks/usePermission";
import {
  type AddressDto,
  type AddressSuggestionDto,
  Permission,
} from "Infrastructure/Api/Api";
import { AccessRightCodes } from "Models/AccessRightCodes";
import { useState } from "react";
import {
  type ArrayPath,
  type Control,
  get,
  type Path,
  useFieldArray,
  useFormContext,
  useFormState,
  useWatch,
} from "react-hook-form";
import { toast } from "sonner";
import { Resources, useResource } from "Translations/Resources";
import { copyAddressDtoToFormFields } from "Utils/AddressUtils";

const COMPONENT_RESOURCES =
  Resources.Pages.Content.UserDetailPageContent.AddressFields;
const REMOVE_RESOURCES = COMPONENT_RESOURCES.Remove;

type AddressFieldsProps<
  T extends PartyFormDataInput,
  U extends PartyFormData,
> = {
  control: Control<T, unknown, U>;
  /**
   * Accepted for caller compatibility but no longer used — the rendered list is driven
   * by form state (useFieldArray) so that removals stay in sync with what gets submitted.
   */
  addresses?: Array<AddressDto>;
  /**
   * When provided, enables per-address removal (back-office only). The owning party's
   * PublicID is needed to call the dedicated delete endpoint and refresh its data.
   * Left undefined by the non-related-party party editors, which stay read/edit-only.
   */
  partyPublicID?: string;
};

type NameGuard = `addresses.${number}.${keyof AddressFormData}`;

type PendingRemoval = { index: number; addressID: number };

export const AddressFields = <
  T extends PartyFormDataInput,
  U extends PartyFormData,
>({
  control,
  partyPublicID,
}: AddressFieldsProps<T, U>) => {
  const addressTypeList = useAddressTypeList();
  const { t } = useResource();
  const { setValue } = useFormContext<T>();
  const watchedAddresses = useWatch({
    control,
    name: "addresses" as Path<T>,
  }) as Array<AddressFormData> | undefined;
  const { errors } = useFormState({ control });

  const getAcceptAddressSuggestionHandler =
    (index: number) => (suggestion: AddressSuggestionDto) => {
      copyAddressDtoToFormFields(suggestion, (field, value) =>
        setValue(
          `addresses.${index}.${field}` satisfies NameGuard as Path<T>,
          value as never,
          { shouldDirty: true, shouldValidate: true },
        ),
      );
    };

  const getClearAddressHandler = (index: number) => () => {
    copyAddressDtoToFormFields({}, (field, value) =>
      setValue(
        `addresses.${index}.${field}` satisfies NameGuard as Path<T>,
        value as never,
        { shouldDirty: true, shouldValidate: true },
      ),
    );
  };

  const { fields, remove } = useFieldArray({
    control,
    name: "addresses" as ArrayPath<T>,
  });

  const canRemove = useCheckPermission(
    AccessRightCodes.Parties,
    Permission.Delete,
  );
  const confirmDialog = useConfirmDialog();
  const [pendingRemoval, setPendingRemoval] = useState<PendingRemoval | null>(
    null,
  );

  const { mutate: deleteAddress, isPending: isDeleting } =
    useDeletePartyAddressMutation({ partyPublicID: partyPublicID ?? "" });

  // Removal is offered only to back-office (Parties Delete) and never for the last
  // remaining address — a party must always keep at least one.
  const isRemovalEnabled = !!partyPublicID && canRemove && fields.length > 1;

  const requestRemove = (index: number, addressID: number) => {
    setPendingRemoval({ index, addressID });
    confirmDialog.open();
  };

  const handleConfirmedRemove = () => {
    if (!pendingRemoval) {
      return;
    }

    const { index, addressID } = pendingRemoval;
    deleteAddress(
      { addressID },
      {
        onSuccess: () => {
          // Keep form state in sync so a later Save doesn't re-create the deleted
          // address (UpdatePartyCommand re-adds any addressID it no longer finds).
          remove(index);
          toast.success(t(REMOVE_RESOURCES.SuccessMessage));
        },
        onError: () => toast.error(t(REMOVE_RESOURCES.ErrorMessage)),
      },
    );
  };

  return (
    <>
      {fields.map((field, index) => {
        const addressID = (field as { addressID?: number | null }).addressID;
        const prefix = `addresses.${index}.` as const;
        const title =
          fields.length > 1
            ? `${index + 1}. ${t(COMPONENT_RESOURCES.Legend)}`
            : t(COMPONENT_RESOURCES.Legend);

        return (
          <FormSection legend={title} key={field.id}>
            <Dropdown
              control={control}
              name={`${prefix}type` satisfies NameGuard as Path<T>}
              label={t(COMPONENT_RESOURCES.Type)}
              codeList={addressTypeList}
            />
            <AddressInput
              address={watchedAddresses?.[index]}
              onAcceptSuggestion={getAcceptAddressSuggestionHandler(index)}
              onClear={getClearAddressHandler(index)}
              hasErrors={Boolean(get(errors, `addresses.${index}`))}
            >
              <FormInput
                control={control}
                name={`${prefix}street` satisfies NameGuard as Path<T>}
                label={t(COMPONENT_RESOURCES.Street)}
              />
              <FormInput
                control={control}
                name={`${prefix}streetNumber` satisfies NameGuard as Path<T>}
                label={t(COMPONENT_RESOURCES.StreetNumber)}
              />
              <FormInput
                control={control}
                name={
                  `${prefix}orientationNumber` satisfies NameGuard as Path<T>
                }
                label={t(COMPONENT_RESOURCES.OrientationNumber)}
              />
              <FormInput
                control={control}
                name={`${prefix}postalCode` satisfies NameGuard as Path<T>}
                label={t(COMPONENT_RESOURCES.PostalCode)}
              />
              <FormInput
                control={control}
                name={`${prefix}municipality` satisfies NameGuard as Path<T>}
                label={t(COMPONENT_RESOURCES.Municipality)}
              />
            </AddressInput>
            {isRemovalEnabled && addressID != null && (
              <Box display="flex" justifyContent="flex-end">
                <Button
                  variant="text"
                  color="error"
                  startIcon={<DeleteOutlineIcon />}
                  disabled={isDeleting}
                  onClick={() => requestRemove(index, addressID)}
                >
                  {t(REMOVE_RESOURCES.Button)}
                </Button>
              </Box>
            )}
          </FormSection>
        );
      })}
      <ConfirmDialog
        isDestructive
        isOpen={confirmDialog.isOpen}
        modalTitle={t(REMOVE_RESOURCES.ConfirmTitle)}
        modalContent={t(REMOVE_RESOURCES.ConfirmContent)}
        confirmButtonText={t(REMOVE_RESOURCES.Button)}
        handleResult={confirmDialog.handleResult(handleConfirmedRemove)}
      />
    </>
  );
};
